import { api } from '../client';
import { unwrap } from '../unwrap';
import { putPresignedObject } from '../presignedUpload';
import type { Note, NoteCategory, NoteListResponse } from '@/core/models/note';

export interface ListNotesParams {
  page?: number;
  page_size?: number;
  category?: NoteCategory;
  include_deleted?: boolean;
}

export interface UpdateNotePayload {
  title?: string;
  content?: string;
  category?: NoteCategory;
  tags?: string[];
  base_updated_at?: string;
}

export const notesApi = {
  async list(params: ListNotesParams = {}): Promise<NoteListResponse> {
    const raw = await api.get<unknown>('/api/v1/notes/', { query: params as Record<string, string | number | boolean | undefined> });
    return unwrap<NoteListResponse>(raw);
  },

  async get(id: string): Promise<Note> {
    const raw = await api.get<unknown>(`/api/v1/notes/${id}`);
    return unwrap<Note>(raw);
  },

  async update(id: string, payload: UpdateNotePayload): Promise<Note> {
    const raw = await api.put<unknown>(`/api/v1/notes/${id}`, payload);
    return unwrap<Note>(raw);
  },

  async remove(id: string): Promise<void> {
    await api.delete<unknown>(`/api/v1/notes/${id}`);
  },

  async restore(id: string): Promise<Note> {
    const raw = await api.post<unknown>(`/api/v1/notes/${id}/restore`);
    return unwrap<Note>(raw);
  },

  async permanentRemove(id: string): Promise<void> {
    await api.delete<unknown>(`/api/v1/notes/${id}/permanent`);
  },

  // ----- Image attachments -----

  async getMediaUploadUrl(
    noteId: string,
    fileName: string,
    contentType = 'image/jpeg'
  ): Promise<{
    note_id: string;
    upload_url: string;
    file_url: string;
    object_key: string;
    expires_in: number;
    headers?: Record<string, string>;
  }> {
    const raw = await api.post<unknown>(`/api/v1/notes/${noteId}/media/upload-url`, {
      file_name: fileName,
      content_type: contentType,
      media_type: 'image',
    });
    return unwrap(raw);
  },

  async getDetachedMediaUploadUrl(
    fileName: string,
    contentType = 'image/jpeg'
  ): Promise<{
    upload_url: string;
    file_url: string;
    object_key: string;
    expires_in: number;
    headers?: Record<string, string>;
  }> {
    const raw = await api.post<unknown>('/api/v1/notes/media/upload-url', {
      file_name: fileName,
      content_type: contentType,
      media_type: 'image',
    });
    return unwrap(raw);
  },

  async confirmMediaUpload(
    noteId: string,
    payload: {
      file_url: string;
      object_key?: string;
      etag?: string;
      file_size?: number;
      mime_type?: string;
    }
  ): Promise<{ id: string; url: string; status: string }> {
    const raw = await api.post<unknown>(
      `/api/v1/notes/${noteId}/media/confirm-upload`,
      { ...payload, media_type: 'image' }
    );
    return unwrap(raw);
  },

  async confirmDetachedMediaUpload(payload: {
    file_url: string;
    object_key?: string;
    etag?: string;
    file_size?: number;
    mime_type?: string;
  }): Promise<{
    file_url: string;
    object_key?: string;
    etag?: string;
    file_size?: number;
    mime_type?: string;
    media_type: string;
  }> {
    const raw = await api.post<unknown>('/api/v1/notes/media/confirm-upload', {
      ...payload,
      media_type: 'image',
    });
    return unwrap(raw);
  },

  async deleteMedia(noteId: string, mediaId: string): Promise<void> {
    await api.delete<unknown>(`/api/v1/notes/${noteId}/media/${mediaId}`);
  },

  async uploadImagesToOss(
    uploadUrl: string,
    blob: Blob,
    contentType: string,
    extraHeaders?: Record<string, string>
  ): Promise<{ etag: string | null }> {
    const res = await putPresignedObject({
      uploadUrl,
      body: blob,
      contentType,
      headers: extraHeaders,
    });
    if (!res.ok) throw new Error(`OSS image upload failed: HTTP ${res.status}`);
    // ETag comes back quoted by OSS
    const etag = res.headers.get('etag')?.replace(/"/g, '') ?? null;
    return { etag };
  },
};
